import {
  ProductHistoryPageResponseSchema,
  SearchContentPageResponseSchema,
  SecurityAuditPageResponseSchema,
} from "@oyako-flow/contracts";
import { useCallback, useEffect, useMemo, useState, type SyntheticEvent } from "react";

type SearchMode = "search" | "history" | "audit";
type DocumentType = "task" | "routine" | "project" | "note";

interface SearchItem {
  workspace_id: string;
  document_type: DocumentType;
  document_subtype: "task" | "routine" | "project" | "evidence" | "review";
  document_id: string;
  title: string;
  excerpt: string;
  status: string;
  area_id: string | null;
  project_id: string | null;
  routine_id: string | null;
  has_evidence: boolean;
  local_date: string | null;
  updated_at: string;
}

interface ProductHistoryItem {
  workspace_id: string;
  source_event_id: string | null;
  resource_type: DocumentType;
  resource_id: string;
  resource_title: string;
  event_type: string;
  actor_type: "adult" | "child" | "system" | "agent" | null;
  actor_id: string | null;
  client_type: "web" | "pwa" | "native" | "mcp" | "system" | null;
  details: Record<string, unknown>;
  created_at: string;
}

interface SecurityAuditItem {
  id: string;
  workspace_id: string;
  event_type: string;
  actor_id: string;
  actor_type: "adult" | "child" | "system";
  target_type: string | null;
  target_id: string | null;
  request_id: string;
  outcome: "success" | "failure" | "denied";
  created_at: string;
}

interface ChildOption {
  id: string;
  display_name: string;
}

interface ParentSearchHistoryProps {
  selectedChild: ChildOption | null;
  request: (path: string) => Promise<unknown>;
}

const documentLabels: Record<DocumentType, string> = {
  task: "タスク",
  routine: "ルーティン",
  project: "プロジェクト",
  note: "メモ",
};

function appendRepeated(
  parameters: URLSearchParams,
  name: string,
  values: readonly string[],
): void {
  for (const value of values) parameters.append(name, value);
}

function formatInstant(value: string): string {
  return new Intl.DateTimeFormat("ja-JP", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function readableDetail(value: unknown): string {
  if (value === null) return "なし";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}

export function ParentSearchHistory({ selectedChild, request }: ParentSearchHistoryProps) {
  const [mode, setMode] = useState<SearchMode>("search");
  const [queryDraft, setQueryDraft] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([
    "task",
    "routine",
    "project",
    "note",
  ]);
  const [searchItems, setSearchItems] = useState<SearchItem[]>([]);
  const [historyItems, setHistoryItems] = useState<ProductHistoryItem[]>([]);
  const [auditItems, setAuditItems] = useState<SecurityAuditItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedTypes = useMemo(() => new Set(documentTypes), [documentTypes]);

  const loadPage = useCallback(
    async (cursor: string | null, append: boolean) => {
      setLoading(true);
      setError(null);
      try {
        const parameters = new URLSearchParams({ limit: "20" });
        if (cursor !== null) parameters.set("cursor", cursor);
        if (mode === "search") {
          appendRepeated(parameters, "document_types", documentTypes);
          if (appliedQuery.trim().length > 0) parameters.set("query", appliedQuery.trim());
          if (selectedChild !== null) parameters.set("child_id", selectedChild.id);
          const page = SearchContentPageResponseSchema.parse(
            await request(`/search?${parameters.toString()}`),
          );
          setSearchItems((current) => (append ? [...current, ...page.items] : page.items));
          setNextCursor(page.next_cursor);
        } else if (mode === "history") {
          const page = ProductHistoryPageResponseSchema.parse(
            await request(`/history?${parameters.toString()}`),
          );
          setHistoryItems((current) => (append ? [...current, ...page.items] : page.items));
          setNextCursor(page.next_cursor);
        } else {
          const page = SecurityAuditPageResponseSchema.parse(
            await request(`/security-audit?${parameters.toString()}`),
          );
          setAuditItems((current) => (append ? [...current, ...page.items] : page.items));
          setNextCursor(page.next_cursor);
        }
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "履歴を読み込めませんでした。");
      } finally {
        setLoading(false);
      }
    },
    [appliedQuery, documentTypes, mode, request, selectedChild],
  );

  useEffect(() => {
    const timeout = window.setTimeout(() => void loadPage(null, false), 0);
    return () => window.clearTimeout(timeout);
  }, [loadPage]);

  function submitSearch(event: SyntheticEvent<HTMLFormElement, SubmitEvent>) {
    event.preventDefault();
    if (documentTypes.length === 0) {
      setError("検索対象を1つ以上選んでください。");
      return;
    }
    setAppliedQuery(queryDraft);
  }

  function toggleDocumentType(documentType: DocumentType) {
    setDocumentTypes((current) =>
      current.includes(documentType)
        ? current.filter((value) => value !== documentType)
        : [...current, documentType],
    );
  }

  const visibleCount =
    mode === "search"
      ? searchItems.length
      : mode === "history"
        ? historyItems.length
        : auditItems.length;

  return (
    <div className="parent-view search-history-view">
      <div className="search-history-tabs" role="tablist" aria-label="表示する記録">
        {(
          [
            ["search", "内容を検索"],
            ["history", "変更履歴"],
            ["audit", "セキュリティ監査"],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={mode === value}
            onClick={() => setMode(value)}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === "search" ? (
        <form className="panel search-filter-panel" onSubmit={submitSearch}>
          <label className="search-query-field">
            キーワード
            <input
              type="search"
              value={queryDraft}
              maxLength={200}
              placeholder="タスク、ルーティン、プロジェクト、メモ"
              onChange={(event) => setQueryDraft(event.currentTarget.value)}
            />
          </label>
          <fieldset>
            <legend>検索する種類</legend>
            <div className="search-type-options">
              {(Object.keys(documentLabels) as DocumentType[]).map((documentType) => (
                <label key={documentType}>
                  <input
                    type="checkbox"
                    checked={selectedTypes.has(documentType)}
                    onChange={() => toggleDocumentType(documentType)}
                  />
                  {documentLabels[documentType]}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="search-filter-actions">
            <p>
              {selectedChild === null
                ? "家族全体を検索します"
                : `${selectedChild.display_name}に関係する内容へ絞り込みます`}
            </p>
            <button className="primary" type="submit" disabled={loading}>
              検索する
            </button>
          </div>
        </form>
      ) : mode === "history" ? (
        <section className="panel history-context">
          <h2>家族の変更履歴</h2>
          <p>タスクや計画へ実際に保存された変更だけを、新しい順に表示します。</p>
        </section>
      ) : (
        <section className="panel history-context">
          <h2>セキュリティ監査</h2>
          <p>認証、端末、権限、Agent操作などの記録です。秘密値や子供の本文は表示しません。</p>
        </section>
      )}

      {error === null ? null : (
        <p className="error-banner" role="alert">
          {error}
        </p>
      )}

      {loading && visibleCount === 0 ? (
        <section className="panel loading-panel" aria-busy="true">
          <p className="status" role="status">
            読み込んでいます…
          </p>
        </section>
      ) : visibleCount === 0 ? (
        <section className="panel empty-onboarding">
          <h2>{mode === "search" ? "一致する内容はありません" : "表示できる記録はありません"}</h2>
          <p>
            {mode === "search"
              ? "キーワードや検索対象を変えて、もう一度お試しください。"
              : "この画面へ表示される操作が保存されると、ここで確認できます。"}
          </p>
        </section>
      ) : mode === "search" ? (
        <section className="search-result-list" aria-label="検索結果">
          {searchItems.map((item) => (
            <article
              className="panel search-result-card"
              key={`${item.document_type}:${item.document_id}`}
            >
              <header>
                <span>{documentLabels[item.document_type]}</span>
                <time dateTime={item.updated_at}>{formatInstant(item.updated_at)}</time>
              </header>
              <h2>{item.title}</h2>
              {item.excerpt.length === 0 ? null : <p>{item.excerpt}</p>}
              <footer>
                <span>{item.status}</span>
                {item.local_date === null ? null : <span>{item.local_date}</span>}
                {item.has_evidence ? <span>証跡あり</span> : null}
              </footer>
            </article>
          ))}
        </section>
      ) : mode === "history" ? (
        <ol className="history-timeline" aria-label="変更履歴">
          {historyItems.map((item) => (
            <li
              className="panel history-card"
              key={`${item.created_at}:${item.resource_id}:${item.event_type}`}
            >
              <header>
                <span>{documentLabels[item.resource_type]}</span>
                <time dateTime={item.created_at}>{formatInstant(item.created_at)}</time>
              </header>
              <h2>{item.resource_title}</h2>
              <p className="history-event-name">{item.event_type}</p>
              <p className="history-actor">
                {item.actor_type ?? "不明"} · {item.client_type ?? "不明なクライアント"}
              </p>
              {Object.keys(item.details).length === 0 ? null : (
                <details>
                  <summary>変更内容</summary>
                  <dl>
                    {Object.entries(item.details).map(([key, value]) => (
                      <div key={key}>
                        <dt>{key}</dt>
                        <dd>{readableDetail(value)}</dd>
                      </div>
                    ))}
                  </dl>
                </details>
              )}
            </li>
          ))}
        </ol>
      ) : (
        <ol className="history-timeline" aria-label="セキュリティ監査">
          {auditItems.map((item) => (
            <li className="panel history-card audit-card" key={item.id}>
              <header>
                <span className={`audit-outcome audit-outcome-${item.outcome}`}>
                  {item.outcome === "success"
                    ? "成功"
                    : item.outcome === "denied"
                      ? "拒否"
                      : "失敗"}
                </span>
                <time dateTime={item.created_at}>{formatInstant(item.created_at)}</time>
              </header>
              <h2>{item.event_type}</h2>
              <p>
                {item.actor_type} · {item.target_type ?? "Workspace"}
              </p>
              <small>Request ID: {item.request_id}</small>
            </li>
          ))}
        </ol>
      )}

      {nextCursor === null ? null : (
        <div className="history-load-more">
          <button type="button" disabled={loading} onClick={() => void loadPage(nextCursor, true)}>
            {loading ? "読み込み中…" : "さらに読み込む"}
          </button>
        </div>
      )}
    </div>
  );
}
